import { Circle, Line, type Canvas } from "fabric";

export const ZONE_CLOSE_SNAP_RADIUS = 12;
export const DRAW_VERTEX_RADIUS = 3;

/**
 * Snap a point to the horizontal or vertical axis relative to an anchor.
 * If the horizontal distance is greater, the point snaps to the same Y as anchor (horizontal line).
 * Otherwise, the point snaps to the same X as anchor (vertical line).
 */
export function snapToAxis(
  anchor: { x: number; y: number },
  pt: { x: number; y: number },
): { x: number; y: number } {
  const dx = Math.abs(pt.x - anchor.x);
  const dy = Math.abs(pt.y - anchor.y);
  return dx >= dy ? { x: pt.x, y: anchor.y } : { x: anchor.x, y: pt.y };
}

interface DrawingState {
  points: { x: number; y: number }[];
  tempObjects: any[];
}

/**
 * Check if the given point is near the first point of the zone (snap-close detection).
 */
export function isNearFirstPoint(
  point: { x: number; y: number },
  state: DrawingState,
  zoom: number,
): boolean {
  if (state.points.length < 3) return false;
  const first = state.points[0];
  const dx = (point.x - first.x) * zoom;
  const dy = (point.y - first.y) * zoom;
  return Math.sqrt(dx * dx + dy * dy) <= ZONE_CLOSE_SNAP_RADIUS;
}

/**
 * Remove the existing preview line from canvas and tempObjects.
 */
export function removePreviewLine(canvas: Canvas, state: DrawingState): void {
  const old = state.tempObjects.find((o) => (o as any)._isPreview);
  if (old) {
    canvas.remove(old);
    state.tempObjects = state.tempObjects.filter((o) => o !== old);
  }
}

/**
 * Create a dashed preview line from the last placed point.
 */
export function createPreviewLine(
  canvas: Canvas,
  state: DrawingState,
  color: string,
): void {
  const lastPt = state.points[state.points.length - 1];
  const preview = new Line([lastPt.x, lastPt.y, lastPt.x, lastPt.y], {
    stroke: color,
    strokeWidth: 2,
    selectable: false,
    evented: false,
    strokeDashArray: [5, 5],
  });
  (preview as any)._isPreview = true;
  canvas.add(preview);
  state.tempObjects.push(preview);
}

/**
 * Update the preview line endpoint to follow the cursor.
 */
export function updatePreviewLine(
  canvas: Canvas,
  state: DrawingState,
  pt: { x: number; y: number },
): void {
  if (state.points.length < 1) return;
  const previewLine = state.tempObjects.find((o) => (o as any)._isPreview) as Line | undefined;
  if (previewLine) {
    previewLine.set({ x2: pt.x, y2: pt.y });
    canvas.requestRenderAll();
  }
}

/**
 * Enlarge/shrink the first vertex marker when cursor is near the snap-close point.
 */
export function updateFirstMarkerOnHover(
  canvas: Canvas,
  state: DrawingState,
  pt: { x: number; y: number },
  zoom: number,
): void {
  if (state.points.length < 3) return;
  const isNear = isNearFirstPoint(pt, state, zoom);
  const firstMarker = state.tempObjects.find((o) => o instanceof Circle) as Circle | undefined;
  if (firstMarker) {
    const newRadius = isNear ? DRAW_VERTEX_RADIUS * 2 : DRAW_VERTEX_RADIUS;
    const newStrokeWidth = isNear ? 2 : 1;
    if (firstMarker.radius !== newRadius) {
      firstMarker.set({ radius: newRadius, strokeWidth: newStrokeWidth });
      canvas.requestRenderAll();
    }
  }
}
